import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css"; // Import Bubble theme styles
import ColorPicker from "./ColorPicker"; // Import the ColorPicker component
import "./LessonBuilder.css"; // Import your custom CSS file
import { FaGripLines, FaTrashAlt, FaCopy, FaChevronLeft, FaChevronRight } from "react-icons/fa"; // Import drag, delete, duplicate, and image switcher icons

// Define a custom toolbar with additional options (keep Quill's default clipboard to preserve HTML)
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["code-block"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "color",
  "background",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "align",
  "link",
  "image",
  "video", // Added video
  "code-block", // Added code block
  "script", // Added subscript and superscript
  "direction", // Added text direction
];

// Utility function to convert RGB to hex
const rgbToHex = (r, g, b) => {
  const componentToHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
};

const TemplateEditor = ({
  template,
  sectionIndex,
  onDelete,
  onSave,
  onDuplicate,
  onRegenerateImages,
  isGeneratingImages,
  dragHandleProps,
  isSelectedForSectionImage = false,
  onSelectForSectionImage,
  onSectionImageUpdate,
}) => {
  const [editTemplate, setEditTemplate] = useState(template);
  const checkboxId = template?.id
    ? `section-image-${template.id}`
    : `section-image-${sectionIndex}`;
  const notifySectionImageUpdate = (imageUrl) => {
    if (isSelectedForSectionImage && typeof onSectionImageUpdate === "function") {
      onSectionImageUpdate(imageUrl || "");
    }
  };

  // Sync local state when parent template updates (e.g., AI-generated images)
  useEffect(() => {
    setEditTemplate(template);
  }, [template]);

  const handleContentChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[miniIndex].content = value;
    } else {
      newTemplate.subSections[subIndex].content = value;
    }
    setEditTemplate(newTemplate);
  };

  const handleLottieFilePathChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[miniIndex].lottieFilePath =
        value;
    } else {
      newTemplate.subSections[subIndex].lottieFilePath = value;
    }
    setEditTemplate(newTemplate);
  };

  const handleImageFilePathChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[miniIndex].imageFilePath =
        value;
    } else {
      newTemplate.subSections[subIndex].imageFilePath = value;
    }
    setEditTemplate(newTemplate);
    notifySectionImageUpdate(value);
  };

  const handleDelayChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[
        miniIndex
      ].transition.delay = value;
    } else {
      newTemplate.subSections[subIndex].transition.delay = value;
    }
    setEditTemplate(newTemplate);
  };

  const handleMaxWidthChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[miniIndex].style.maxWidth =
        value;
    } else {
      newTemplate.subSections[subIndex].style.maxWidth = value;
    }
    setEditTemplate(newTemplate);
  };

  const handleWidthChange = (subIndex, value, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    if (miniIndex !== null) {
      newTemplate.subSections[subIndex].miniSections[miniIndex].style.width =
        value;
    } else {
      newTemplate.subSections[subIndex].style.width = value;
    }
    setEditTemplate(newTemplate);
  };

  const handleBackgroundColorChange = (color) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);
    newTemplate.style = { ...(newTemplate.style || {}), backgroundColor: hexColor };
    setEditTemplate(newTemplate);
  };

  const handleSubSectionBackgroundColorChange = (subIndex, color) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);
    const sub = newTemplate.subSections[subIndex];
    if (!sub) return;
    sub.style = { ...(sub.style || {}), backgroundColor: hexColor };
    setEditTemplate(newTemplate);
  };

  const handleContentStyleBackgroundChange = (subIndex, color) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);
    newTemplate.subSections[subIndex].contentStyle = {
      ...newTemplate.subSections[subIndex].contentStyle,
      background: hexColor,
    };
    setEditTemplate(newTemplate);
  };

  const handleAnimateBackgroundColorChange = (subIndex, color) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);
    newTemplate.subSections[subIndex].animate = {
      ...newTemplate.subSections[subIndex].animate,
      background: hexColor,
    };
    setEditTemplate(newTemplate);
  };

  const handleSubSectionBorderColorAndContentColorChange = (
    subIndex,
    color
  ) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);

    // Update the border color
    newTemplate.subSections[subIndex].style = {
      ...newTemplate.subSections[subIndex].style,
      border: `10px solid ${hexColor}`,
    };

    // Update the contentStyle color
    newTemplate.subSections[subIndex].contentStyle = {
      ...newTemplate.subSections[subIndex].contentStyle,
      color: hexColor,
    };

    setEditTemplate(newTemplate);
  };

  const extractBorderColor = (border) => {
    if (!border || typeof border !== "string") return "#000000";
    const result = border.match(
      /10px solid (#[0-9a-f]{6}|#[0-9a-f]{3}|rgb\(.+\))/i
    );
    return result ? result[1] : "#000000"; // default to black if no match
  };

  const handleMiniSectionBackgroundColorChange = (
    subIndex,
    miniIndex,
    color
  ) => {
    const newTemplate = { ...editTemplate };
    const hexColor = rgbToHex(color.r, color.g, color.b);

    // Update the background color of the specific miniSection
    newTemplate.subSections[subIndex].miniSections[miniIndex].style = {
      ...newTemplate.subSections[subIndex].miniSections[miniIndex].style,
      backgroundColor: hexColor,
    };

    setEditTemplate(newTemplate);
  };
  const handleVideoLinkChange = (subIndex, value) => {
    const newTemplate = { ...editTemplate };
    newTemplate.subSections[subIndex].videoLink = value;
    setEditTemplate(newTemplate);
  };

  // Handle switching between image options
  const handleImageOptionChange = (subIndex, direction, miniIndex = null) => {
    const newTemplate = { ...editTemplate };
    let updatedImagePath = null;
    
    if (miniIndex !== null) {
      // Handle miniSection image
      const miniSection = newTemplate.subSections[subIndex].miniSections[miniIndex];
      if (!miniSection.imageOptions || miniSection.imageOptions.length === 0) return;
      
      const currentIndex = miniSection.selectedImageIndex || 0;
      let newIndex;
      
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % miniSection.imageOptions.length;
      } else {
        newIndex = (currentIndex - 1 + miniSection.imageOptions.length) % miniSection.imageOptions.length;
      }
      
      miniSection.selectedImageIndex = newIndex;
      miniSection.imageFilePath = miniSection.imageOptions[newIndex];
      updatedImagePath = miniSection.imageFilePath;
    } else {
      // Handle regular subSection image
      const subSection = newTemplate.subSections[subIndex];
      if (!subSection.imageOptions || subSection.imageOptions.length === 0) return;
      
      const currentIndex = subSection.selectedImageIndex || 0;
      let newIndex;
      
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % subSection.imageOptions.length;
      } else {
        newIndex = (currentIndex - 1 + subSection.imageOptions.length) % subSection.imageOptions.length;
      }
      
      subSection.selectedImageIndex = newIndex;
      subSection.imageFilePath = subSection.imageOptions[newIndex];
      updatedImagePath = subSection.imageFilePath;
    }
    
    setEditTemplate(newTemplate);
    if (updatedImagePath !== null) {
      notifySectionImageUpdate(updatedImagePath);
    }
  };

  useEffect(() => {
    onSave(editTemplate);
  }, [editTemplate]);

  const handleDelete = () => {
    onDelete();
  };

  const handleDuplicate = () => {
    onDuplicate(editTemplate);
  };

  // Check if this template has image placeholders
  const hasImagePlaceholders = () => {
    return (editTemplate.subSections || []).some(sub =>
      (sub.className && sub.className.includes('image')) ||
      (sub.miniSections && sub.miniSections.some(mini => mini.className && mini.className.includes('image')))
    );
  };

  return (
    <div className="template-editor-container">
      <div className="template-editor-content">
        {editTemplate.name === "HT1L1B" && (
          <ColorPicker
            color={editTemplate.style?.backgroundColor || ""}
            onChange={handleBackgroundColorChange}
          />
        )}

        {editTemplate.name === "HT2B" && (
          <>
            <ColorPicker
              color={editTemplate.subSections?.[0]?.style?.backgroundColor || ""}
              onChange={(color) =>
                handleSubSectionBackgroundColorChange(0, color)
              }
            />
            <ColorPicker
              color={editTemplate.subSections?.[2]?.style?.backgroundColor || ""}
              onChange={(color) =>
                handleSubSectionBackgroundColorChange(2, color)
              }
            />
          </>
        )}

        {editTemplate.name === "VT3Highlight" && (
          <ColorPicker
            color={editTemplate.subSections?.[1]?.animate?.background || ""}
            onChange={(color) => handleAnimateBackgroundColorChange(1, color)}
          />
        )}

        {editTemplate.name === "VT2BB" && (
          <>
            <ColorPicker
              color={editTemplate.subSections?.[0]?.contentStyle?.background || ""}
              onChange={(color) => handleContentStyleBackgroundChange(0, color)}
            />
            <ColorPicker
              color={editTemplate.subSections?.[1]?.contentStyle?.background || ""}
              onChange={(color) => handleContentStyleBackgroundChange(1, color)}
            />
          </>
        )}
        {editTemplate.name === "MVT1HL1T1B" && (
          <ColorPicker
            color={editTemplate.subSections?.[1]?.style?.backgroundColor || ""}
            onChange={(color) =>
              handleSubSectionBackgroundColorChange(1, color)
            }
          />
        )}
        {editTemplate.name === "VT2B" && (
          <ColorPicker
            color={editTemplate.subSections?.[1]?.contentStyle?.background || ""}
            onChange={(color) => handleContentStyleBackgroundChange(1, color)}
          />
        )}
        {editTemplate.name === "VT2Definition" && (
          <ColorPicker
            color={editTemplate.subSections?.[1]?.style?.backgroundColor || ""}
            onChange={(color) =>
              handleSubSectionBackgroundColorChange(1, color)
            }
          />
        )}
        {editTemplate.name === "Number Card" && (
          <ColorPicker
            color={extractBorderColor(editTemplate.subSections?.[0]?.style?.border)}
            onChange={(color) =>
              handleSubSectionBorderColorAndContentColorChange(0, color)
            }
          />
        )}
        {editTemplate.name === "VT1Definition" && (
          <ColorPicker
            color={editTemplate.subSections?.[0]?.style?.backgroundColor || ""}
            onChange={(color) =>
              handleSubSectionBackgroundColorChange(0, color)
            }
          />
        )}
        {editTemplate.name === "Three Things" &&
          (editTemplate.subSections?.[0]?.miniSections || []).map(
            (miniSection, miniIndex) => (
              <div key={miniIndex}>
                <ColorPicker
                  color={miniSection.style?.backgroundColor || ""}
                  onChange={(color) =>
                    handleMiniSectionBackgroundColorChange(0, miniIndex, color)
                  }
                />
              </div>
            )
          )}
        {editTemplate.name === "Three Things 2" &&
          (editTemplate.subSections?.[1]?.miniSections || []).map(
            (miniSection, miniIndex) => (
              <div key={miniIndex}>
                <ColorPicker
                  color={miniSection.style?.backgroundColor || ""}
                  onChange={(color) =>
                    handleMiniSectionBackgroundColorChange(1, miniIndex, color)
                  }
                />
              </div>
            )
          )}

        {editTemplate.name === "HT1I1B" && (
          <ColorPicker
            color={editTemplate.style?.backgroundColor || ""}
            onChange={handleBackgroundColorChange}
          />
        )}

        {editTemplate.name === "HI1T1B" && (
          <ColorPicker
            color={editTemplate.style?.backgroundColor || ""}
            onChange={handleBackgroundColorChange}
          />
        )}

        {editTemplate.name === "Definition with image" && (
          <ColorPicker
            color={editTemplate.style?.backgroundColor || ""}
            onChange={handleBackgroundColorChange}
          />
        )}

        {(editTemplate.subSections || []).map((sub, subIndex) => (
          <div key={subIndex}>
            {sub.miniSections && sub.miniSections.length > 0 ? (
              <div>
                {sub.miniSections.map((mini, miniIndex) => (
                  <div key={miniIndex}>
                    {mini.className === "LottieAnimation" ? (
                      <>
                        <div className="input-group">
                          <h5>Lottie file path</h5>
                          <input
                            type="text"
                            value={mini.lottieFilePath || ""}
                            onChange={(e) =>
                              handleLottieFilePathChange(
                                subIndex,
                                e.target.value,
                                miniIndex
                              )
                            }
                            placeholder="Edit Lottie File Path"
                          />
                        </div>
                        <div className="input-group">
                          <h5>Lottie size</h5>
                          <input
                            type="text"
                            value={mini.style?.width || ""}
                            onChange={(e) =>
                              handleWidthChange(
                                subIndex,
                                e.target.value,
                                miniIndex
                              )
                            }
                            placeholder="Edit Width"
                          />
                        </div>
                      </>
                    ) : mini.className === "sectionDescription image" ? (
                      <>
                        <div className="input-group">
                          <h5>Image file path</h5>
                          <input
                            type="text"
                            value={mini.imageFilePath || ""}
                            onChange={(e) =>
                              handleImageFilePathChange(
                                subIndex,
                                e.target.value,
                                miniIndex
                              )
                            }
                            placeholder="Edit Image File Path"
                          />
                        </div>
                        
                        {/* Image Switcher - Shows if AI generated options exist */}
                        {mini.imageOptions && mini.imageOptions.length > 0 && (
                          <div className="image-switcher-container">
                            <h5>AI Generated Options</h5>
                            <div className="image-preview">
                              <img 
                                src={mini.imageFilePath || mini.imageOptions[0]} 
                                alt="Template Preview" 
                                style={{ maxWidth: '200px', height: 'auto' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div style={{ display: 'none', padding: '20px', background: '#f0f0f0', textAlign: 'center' }}>
                                Image loading...
                              </div>
                            </div>
                            <div className="image-switcher-controls">
                              <button 
                                className="image-nav-button"
                                onClick={() => handleImageOptionChange(subIndex, 'prev', miniIndex)}
                                disabled={mini.imageOptions.length <= 1}
                              >
                                <FaChevronLeft />
                              </button>
                              <span className="image-option-indicator">
                                Option {(mini.selectedImageIndex || 0) + 1} of {mini.imageOptions.length}
                              </span>
                              <button 
                                className="image-nav-button"
                                onClick={() => handleImageOptionChange(subIndex, 'next', miniIndex)}
                                disabled={mini.imageOptions.length <= 1}
                              >
                                <FaChevronRight />
                              </button>
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                              {mini.imageOptions.map((url, idx) => (
                                <div key={idx} style={{ 
                                  padding: '4px',
                                  background: idx === (mini.selectedImageIndex || 0) ? '#e0f0ff' : 'transparent',
                                  borderRadius: '3px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {idx + 1}: {url.substring(url.lastIndexOf('/') + 1)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="input-group">
                          <h5>Image size</h5>
                          <input
                            type="text"
                            value={mini.style?.maxWidth || ""}
                            onChange={(e) =>
                              handleMaxWidthChange(
                                subIndex,
                                e.target.value,
                                miniIndex
                              )
                            }
                            placeholder="Edit Max Width"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="reactquill-input-group">
                          <h5>Text</h5>
                          <ReactQuill
                            className="custom-quill-editor"
                            theme="bubble" // Use bubble theme
                            value={mini.content || ""}
                            onChange={(value) =>
                              handleContentChange(subIndex, value, miniIndex)
                            }
                            placeholder=""
                            modules={modules}
                            formats={formats}
                          />
                        </div>
                      </>
                    )}
                    <div className="input-group">
                      <h5>Delay</h5>
                      <input
                        type="number"
                        value={mini.transition?.delay || 0}
                        onChange={(e) =>
                          handleDelayChange(subIndex, e.target.value, miniIndex)
                        }
                        placeholder="Edit Delay"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {sub.className === "LottieAnimation" ? (
                  <>
                    <div className="input-group">
                      <h5>Lottie file path</h5>
                      <input
                        type="text"
                        value={sub.lottieFilePath || ""}
                        onChange={(e) =>
                          handleLottieFilePathChange(subIndex, e.target.value)
                        }
                        placeholder="Edit Lottie File Path"
                      />
                    </div>
                    <div className="input-group">
                      <h5>Lottie size</h5>
                      <input
                        type="text"
                        value={sub.style?.width || ""}
                        onChange={(e) =>
                          handleWidthChange(subIndex, e.target.value)
                        }
                        placeholder="Edit Width"
                      />
                    </div>
                  </>
                ) : sub.className === "sectionDescription image" ? (
                  <>
                    <div className="input-group">
                      <h5>Image file path</h5>
                      <input
                        type="text"
                        value={sub.imageFilePath || ""}
                        onChange={(e) =>
                          handleImageFilePathChange(subIndex, e.target.value)
                        }
                        placeholder="Edit Image File Path"
                      />
                    </div>
                    
                    {/* Image Switcher - Shows if AI generated options exist */}
                    {sub.imageOptions && sub.imageOptions.length > 0 && (
                      <div className="image-switcher-container">
                        <h5>AI Generated Options</h5>
                        <div className="image-preview">
                          <img 
                            src={sub.imageFilePath || sub.imageOptions[0]} 
                            alt="Template Preview" 
                            style={{ maxWidth: '200px', height: 'auto' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none', padding: '20px', background: '#f0f0f0', textAlign: 'center' }}>
                            Image loading...
                          </div>
                        </div>
                        <div className="image-switcher-controls">
                          <button 
                            className="image-nav-button"
                            onClick={() => handleImageOptionChange(subIndex, 'prev')}
                            disabled={sub.imageOptions.length <= 1}
                          >
                            <FaChevronLeft />
                          </button>
                          <span className="image-option-indicator">
                            Option {(sub.selectedImageIndex || 0) + 1} of {sub.imageOptions.length}
                          </span>
                          <button 
                            className="image-nav-button"
                            onClick={() => handleImageOptionChange(subIndex, 'next')}
                            disabled={sub.imageOptions.length <= 1}
                          >
                            <FaChevronRight />
                          </button>
                        </div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                          {sub.imageOptions.map((url, idx) => (
                            <div key={idx} style={{ 
                              padding: '4px',
                              background: idx === (sub.selectedImageIndex || 0) ? '#e0f0ff' : 'transparent',
                              borderRadius: '3px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {idx + 1}: {url.substring(url.lastIndexOf('/') + 1)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="input-group">
                      <h5>Image size</h5>
                      <input
                        type="text"
                        value={sub.style?.maxWidth || ""}
                        onChange={(e) =>
                          handleMaxWidthChange(subIndex, e.target.value)
                        }
                        placeholder="Edit Max Width"
                      />
                    </div>
                  </>
                ) : sub.className === "YouTubeVideo" ? (
                  <input
                    type="text"
                    value={editTemplate.subSections?.[0]?.videoLink || ""}
                    onChange={(e) => handleVideoLinkChange(0, e.target.value)}
                    placeholder="Edit Video Link"
                  />
                ) : (
                  <>
                    <div className="reactquill-input-group">
                      <h5>Text</h5>
                      <ReactQuill
                        className="custom-quill-editor"
                        theme="bubble" // Use bubble theme
                        value={sub.content || ""}
                        onChange={(value) =>
                          handleContentChange(subIndex, value)
                        }
                        placeholder=""
                        modules={modules}
                        formats={formats}
                      />
                    </div>
                  </>
                )}
                {sub.className !== "YouTubeVideo" && (
                  <div className="input-group">
                    <h5>Delay</h5>
                    <input
                      type="number"
                      value={sub.transition?.delay || 0}
                      onChange={(e) =>
                        handleDelayChange(subIndex, e.target.value)
                      }
                      placeholder="Edit Delay"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {hasImagePlaceholders() && onSelectForSectionImage && (
        <div className="section-image-checkbox-container">
          <input
            type="checkbox"
            id={checkboxId}
            checked={isSelectedForSectionImage}
            onChange={() => onSelectForSectionImage()}
          />
          <div className="section-image-checkbox-label" onClick={() => onSelectForSectionImage()} style={{ display: "inline-block", cursor: "pointer", marginLeft: "8px" }}>
            Use as lesson image
          </div>
        </div>
      )}
      <div className="template-editor-buttons">
        {hasImagePlaceholders() && onRegenerateImages && (
          <button 
            className="regenerate-images-btn" 
            onClick={onRegenerateImages}
            disabled={isGeneratingImages}
            title="Generate more image options for this section"
          >
            {isGeneratingImages ? 'Generating...' : 'Regenerate Images'}
          </button>
        )}
        <button className="template-editor-button" onClick={handleDelete}>
          <FaTrashAlt />
        </button>
        <button className="template-editor-button" onClick={handleDuplicate}>
          <FaCopy />
        </button>
      </div>
      <div className="template-editor-handle" {...dragHandleProps}>
        <FaGripLines />
      </div>
    </div>
  );
};

export default TemplateEditor;
